/*
  Nubra 2.0 — comportamentul cardului de produs și al barei „x din 3 alese”.

  1. Popup „Note comune cu”: un singur <dialog> pe pagină, umplut din data-* ale butonului
     [data-nb-insp] (snippets/nubra-card-insp.liquid).
  2. „+ Adaugă” rapid pe card ([data-nb-add]): același drum ca product-form-component —
     un CartLinesUpdateEvent cu promisiune, rezolvat cu secțiunile randate de /cart/add,
     așa că bulina din header și sertarul se actualizează, iar sertarul se deschide singur
     (setarea temei auto_open_cart_drawer).
  3. <nubra-bundle-bar>: câte parfumuri sunt în setul curent de 3 (din /cart.js), ascultă
     evenimentele coșului.
*/
import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';

/* ---------------- 1. popup „note comune cu” ---------------- */
function inspDialog() {
  let d = document.getElementById('nb-ci-modal');
  if (d) return d;
  d = document.createElement('dialog');
  d.id = 'nb-ci-modal';
  d.className = 'nb-ci-modal';
  d.setAttribute('aria-labelledby', 'nb-ci-modal-t');
  d.innerHTML = `<div class="nb-ci-modal__inner">
      <button type="button" class="nb-ci-modal__close" data-nb-insp-close aria-label="Închide">
        <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12" stroke="currentColor" stroke-width="1.6" fill="none" stroke-linecap="round"/></svg>
      </button>
      <p class="nb-ci-modal__eyebrow" data-f="eyebrow"></p>
      <h2 class="nb-ci-modal__title" id="nb-ci-modal-t" data-f="title"></h2>
      <div class="nb-ci-modal__pair">
        <figure><span class="nb-ci-modal__img"><img data-f="product-img" alt="" width="500" height="500" loading="lazy"></span><figcaption data-f="product"></figcaption></figure>
        <figure><span class="nb-ci-modal__img"><img data-f="photo" alt="" width="500" height="500" loading="lazy"></span><figcaption data-f="original"></figcaption></figure>
      </div>
      <p class="nb-ci-modal__text" data-f="text"></p>
      <p class="nb-ci-modal__legal" data-f="legal"></p>
    </div>`;
  document.body.appendChild(d);
  d.addEventListener('click', (e) => {
    if (e.target === d || (e.target instanceof Element && e.target.closest('[data-nb-insp-close]'))) d.close();
  });
  return d;
}

function openInsp(btn) {
  const d = inspDialog();
  const ds = btn.dataset;
  const set = (f, v) => { const el = d.querySelector(`[data-f="${f}"]`); if (el) el.textContent = v || ''; };
  set('eyebrow', ds.eyebrow);
  set('title', ds.title);
  set('product', ds.product);
  set('original', [ds.original, ds.house].filter(Boolean).join(' · '));
  set('text', ds.text);
  set('legal', ds.legal);
  const pimg = d.querySelector('[data-f="product-img"]');
  const oimg = d.querySelector('[data-f="photo"]');
  pimg.hidden = !ds.productImg; if (ds.productImg) { pimg.src = ds.productImg; pimg.alt = ds.product || ''; }
  oimg.hidden = !ds.photo; if (ds.photo) { oimg.src = ds.photo; oimg.alt = ds.original || ''; }
  d.__opener = btn;
  if (!d.open) d.showModal();
  d.addEventListener('close', () => d.__opener?.focus(), { once: true });
}

/* ---------------- 2. „+ Adaugă” rapid ---------------- */
async function quickAdd(button) {
  const variantId = button.dataset.variantId;
  if (!variantId || button.disabled || button.__nbBusy) return;
  button.__nbBusy = true;
  /* MES 3.0 INP (13 oct): scrierile în DOM merg în cadrul următor (nu forțează recalculul de stil al altor ascultători de click),
     iar evenimentele coșului + cererea pleacă într-un task separat, după ce s-a desenat starea „se adaugă”. */
  await new Promise((r) => requestAnimationFrame(() => { button.disabled = true; button.classList.add('is-loading'); setTimeout(r, 0); }));

  const sectionIds = [];
  for (const c of document.querySelectorAll('cart-items-component')) {
    if (c instanceof HTMLElement && c.dataset.sectionId) sectionIds.push(c.dataset.sectionId);
  }
  const deferred = CartLinesUpdateEvent.createPromise();
  button.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: variantId, quantity: 1 }],
      promise: deferred.promise,
    })
  );
  const root = window.Theme?.routes?.cart_add_url || '/cart/add.js';
  fetch(root, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }], sections: sectionIds.join(',') }),
  })
    .then((r) => r.json())
    .then(async (response) => {
      if (response.status) {
        const msg = response.message || button.dataset.errorText || '';
        button.dispatchEvent(new CartErrorEvent({ error: msg, code: 'INVALID', detail: { description: response.description } }));
        deferred.reject(new Error(msg));
        return;
      }
      const cartUrl = window.Theme?.routes?.cart_url || '/cart';
      const cart = await fetch(`${cartUrl}.json`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' }).then((r) => r.json());
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'nubra-card', itemCount: 1, productId: button.dataset.productId, sections: response.sections, didError: false },
      });
      /* măsurare (P10.5): adăugare din card — est-track.js adaugă lista și poziția */
      const estCard = button.closest('[data-est-pid], [data-arona-card]');
      document.dispatchEvent(new CustomEvent('est:atc', { detail: { source: 'card', productId: button.dataset.productId, el: button, name: button.dataset.estName || estCard?.dataset.estName || '', price: Number(button.dataset.estPrice || estCard?.dataset.estPrice || 0), items: [{ id: Number(variantId), quantity: 1 }] } }));
      button.classList.add('is-added');
      const label = button.querySelector('[data-nb-add-label]');
      if (label && button.dataset.addedText) {
        const prev = label.textContent;
        label.textContent = button.dataset.addedText;
        setTimeout(() => { label.textContent = prev; button.classList.remove('is-added'); }, 1800);
      }
    })
    .catch((error) => {
      deferred.reject(error);
      button.dispatchEvent(new CartErrorEvent({ error: error?.message || '', code: 'SERVICE_UNAVAILABLE' }));
    })
    .finally(() => {
      button.disabled = false;
      button.classList.remove('is-loading');
      button.__nbBusy = false;
    });
}

if (!window.__nubraCard) {
  window.__nubraCard = true;
  document.addEventListener('click', (e) => {
    const t = e.target;
    if (!(t instanceof Element)) return;
    const insp = t.closest('[data-nb-insp]');
    if (insp) { e.preventDefault(); e.stopPropagation(); openInsp(insp); return; }
    const add = t.closest('[data-nb-add]');
    if (add instanceof HTMLButtonElement) { e.preventDefault(); quickAdd(add); }
  }, true);
}

/* ---------------- 3. bara „x din 3 alese” ---------------- */
if (!customElements.get('nubra-bundle-bar')) {
  class NubraBundleBar extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this._sig = null;
      this.refresh = this.refresh.bind(this);
      this.settle = this.settle.bind(this);
      /* evenimentele coșului pot veni înainte ca /cart.js să reflecte schimbarea: recitim până se schimbă */
      for (const n of [StandardEvents?.cartLinesUpdate, 'shopify:cart:lines-update', 'cart:update', 'cart-section:restored'].filter(Boolean)) {
        document.addEventListener(n, this.settle);
      }
      /* fără MutationObserver pe bulina coșului: declanșa bucle paralele de /cart.js în timpul modificărilor de coș */
      window.addEventListener('pageshow', this.refresh);
      this.refresh();
    }
    async settle() {
      /* o singură buclă de re-citire odată (evenimentele coșului vin în rafale) */
      if (this._settling) { this._again = true; return; }
      this._settling = true;
      try { await this._settle(); } finally { this._settling = false; }
      if (this._again) { this._again = false; this.settle(); }
    }
    async _settle() {
      const before = this._sig;
      for (const wait of [150, 400, 800, 1500]) {
        await new Promise((r) => setTimeout(r, wait));
        await this.refresh();
        if (this._sig !== before) return;
      }
    }
    async refresh() {
      try {
        const r = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js?t=${Date.now()}`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
        const cart = await r.json();
        this._sig = `${cart.item_count}|${cart.total_price}`;
        const size = Number(this.getAttribute('size') || 3);
        const max = Number(this.dataset.maxPrice || 0);
        const min = Number(this.dataset.minPrice || 0);
        /* doar parfumurile din ofertă: preț între min și max (mostrele de 0 lei și seturile nu se numără) */
        const count = (cart.items || []).reduce((n, it) => {
          const p = it.original_price ?? it.price;
          return n + ((!max || p <= max) && (!min || p >= min) ? it.quantity : 0);
        }, 0);
        const inSet = count === 0 ? 0 : count % size || size;
        const sets = Math.floor(count / size);
        this.querySelectorAll('[data-slot]').forEach((s, i) => s.classList.toggle('is-full', i < inSet));
        const n = this.querySelector('[data-n]');
        if (n) n.textContent = String(inSet);
        const msg = this.querySelector('[data-msg]');
        if (msg) {
          const ds = this.dataset;
          let tpl = ds.msgEmpty;
          if (inSet === size) tpl = count >= size * 2 ? ds.msgDouble || ds.msgDone : ds.msgDone;
          else if (inSet > 0) tpl = size - inSet === 1 ? ds.msgOne || ds.msgLeft : ds.msgLeft;
          msg.textContent = (tpl || '').replace('[n]', String(size - inSet)).replace('[seturi]', String(sets));
        }
        this.classList.toggle('has-items', count > 0);
        this.classList.toggle('is-done', inSet === size);
        this.hidden = false;
      } catch (e) {
        /* coș indisponibil: bara rămâne cum e */
      }
    }
  }
  customElements.define('nubra-bundle-bar', NubraBundleBar);
}
